import styled from '@emotion/styled';
import type { ReactNode } from 'react';

interface InputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: 'text' | 'tel';
  prefix?: string | ReactNode;
  suffix?: ReactNode;
  disabled?: boolean;
  className?: string;
  maxLength?: number;
  pattern?: string;
}

const InputContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  border: 1px solid ${(props) => props.theme.colors.neutral.greyDark[800]};
  border-radius: ${(props) => props.theme.shape.borderRadiusLarge};
  background: transparent;
  overflow: hidden;
`;

const Prefix = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  padding: ${(props) => props.theme.spacing(1.5)} ${(props) => props.theme.spacing(2)};
  background: ${(props) => props.theme.colors.tertiary[900]};
  color: ${(props) => props.theme.colors.neutral.white};
  font-family: ${(props) => props.theme.typography.fontFamily};
  font-size: ${(props) => props.theme.typography.body3Bold.fontSize};
  font-weight: ${(props) => props.theme.typography.body3Bold.fontWeight};
  white-space: nowrap;
  flex-shrink: 0;
`;

const StyledInput = styled.input`
  flex: 1;
  padding: ${(props) => props.theme.spacing(1.5)} ${(props) => props.theme.spacing(2)};
  border: none;
  background: transparent;
  color: ${(props) => props.theme.colors.neutral.white};
  font-family: ${(props) => props.theme.typography.fontFamily};
  font-size: ${(props) => props.theme.typography.body3Regular.fontSize};
  font-weight: ${(props) => props.theme.typography.body3Regular.fontWeight};
  outline: none;

  &::placeholder {
    color: ${(props) => props.theme.colors.text.disabled};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Suffix = styled.div`
  padding: ${(props) => props.theme.spacing(1.5)} ${(props) => props.theme.spacing(2)};
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const Input = ({
  value,
  onChange,
  placeholder,
  type = 'text',
  prefix,
  suffix,
  disabled = false,
  className,
  maxLength,
  pattern,
}: InputProps) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let newValue = e.target.value;

    // Si hay un pattern, validar contra él
    if (pattern) {
      const regex = new RegExp(pattern);
      if (!regex.test(newValue) && newValue !== '') {
        return; // No actualizar si no cumple el patrón
      }
    }

    // Si hay maxLength, truncar el valor
    if (maxLength && newValue.length > maxLength) {
      newValue = newValue.slice(0, maxLength);
    }

    onChange(newValue);
  };

  return (
    <InputContainer className={className}>
      {prefix && <Prefix>{prefix}</Prefix>}
      <StyledInput
        type={type}
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        disabled={disabled}
        maxLength={maxLength}
      />
      {suffix && <Suffix>{suffix}</Suffix>}
    </InputContainer>
  );
};
